import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito";
import TopBar from "../components/TopBar";
import { PortalRole, roleFromTokenClaims } from "../auth/portalAccess";
import { TenantUser, listUsers } from "../services/usersService";
import "./Dashboard.css";

type Props = {
  user?: any;
  onSignOut?: () => void;
};

type Camera = {
  cameraId?: string;
  SK?: string;
  name?: string;
  enabled?: boolean;
  archiveHours?: number;
  motionEventsEnabled?: boolean;
  motionEventsStatus?: string;
  p2pStatus?: string;
  videoStatus?: string;
  streamingStatus?: string;
  planName?: string;
  planType?: string;
  assignedTo?: string;
  createdAt?: string;
  updatedAt?: string;
};

type CameraState = "online" | "offline" | "unknown" | "disabled";

type CameraListResponse = {
  items?: Camera[];
  count?: number;
};

const API_BASE =
  process.env.REACT_APP_API_BASE_URL ||
  "https://f0usr04663.execute-api.us-east-1.amazonaws.com";

function getEmail(user: any): string {
  return (
    user?.signInDetails?.loginId ||
    user?.attributes?.email ||
    user?.username ||
    ""
  );
}

function getCameraId(camera: Camera): string {
  const directId = String(camera.cameraId || "").trim();
  if (directId) return directId;
  return String(camera.SK || "").replace(/^CAMERA#/, "").trim();
}

function getCameraName(camera: Camera): string {
  const id = getCameraId(camera);
  return String(camera.name || (id ? `Camera ${id}` : "Unnamed camera"));
}

function getCameraState(camera: Camera): CameraState {
  if (camera.enabled === false) return "disabled";

  const p2pStatus = String(camera.p2pStatus || "").trim().toLowerCase();
  const videoStatus = String(camera.videoStatus || "").trim().toLowerCase();
  const streamingStatus = String(camera.streamingStatus || "")
    .trim()
    .toLowerCase();

  if (
    p2pStatus.includes("disconnected") ||
    p2pStatus.includes("offline") ||
    p2pStatus.includes("not connected") ||
    p2pStatus.includes("registered") ||
    videoStatus.includes("offline") ||
    streamingStatus.includes("offline") ||
    streamingStatus.includes("error") ||
    streamingStatus.includes("failed")
  ) {
    return "offline";
  }

  if (
    p2pStatus.includes("connected") ||
    p2pStatus.includes("online") ||
    videoStatus.includes("online") ||
    streamingStatus.includes("online") ||
    streamingStatus.includes("running")
  ) {
    return "online";
  }

  return "unknown";
}

function isMotionCollectionEnabled(camera: Camera): boolean {
  if (typeof camera.motionEventsEnabled === "boolean") {
    return camera.motionEventsEnabled;
  }

  const status = String(camera.motionEventsStatus || "")
    .trim()
    .toUpperCase();

  return [
    "ACTIVE",
    "CONNECTED",
    "CONNECTING",
    "ENABLED",
    "PENDING",
    "RECONNECTING",
  ].includes(status);
}

function hasRecording(camera: Camera): boolean {
  return camera.enabled !== false && Number(camera.archiveHours || 0) > 0;
}

function getPlanName(camera: Camera): string {
  const value = String(camera.planName || camera.planType || "").trim();
  return value || "No plan";
}

function formatRelativeTime(value?: string): string {
  if (!value) return "No recent update";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "No recent update";

  const delta = Date.now() - date.getTime();
  if (delta < 0) return date.toLocaleString();

  const minutes = Math.floor(delta / 60000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;

  return date.toLocaleDateString();
}

function stateLabel(state: CameraState): string {
  if (state === "online") return "Online";
  if (state === "offline") return "Offline";
  if (state === "disabled") return "Disabled";
  return "Unknown";
}

function roleLabel(role: PortalRole | null): string {
  if (!role) return "Admin portal";
  return role.charAt(0) + role.slice(1).toLowerCase();
}

function percentage(value: number, total: number): number {
  if (!total) return 0;
  return Math.round((value / total) * 100);
}

export default function Dashboard({ user, onSignOut }: Props) {
  const email = getEmail(user);
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [users, setUsers] = useState<TenantUser[] | null>(null);
  const [role, setRole] = useState<PortalRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboardError, setDashboardError] = useState("");
  const [usersError, setUsersError] = useState("");
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);

  const loadDashboard = useCallback(async (initialLoad = false) => {
    if (initialLoad) setLoading(true);
    else setRefreshing(true);

    setDashboardError("");
    setUsersError("");

    try {
      const tokens = await cognitoUserPoolsTokenProvider.getTokens();
      const idToken = tokens?.idToken;
      if (!idToken) {
        throw new Error("Your session has expired. Sign out and sign in again.");
      }

      const claims = idToken.payload as Record<string, unknown> | undefined;
      const resolvedRole = roleFromTokenClaims(claims);
      setRole(resolvedRole);

      const response = await fetch(`${API_BASE}/cameras`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${idToken.toString()}`,
        },
      });

      const text = await response.text();
      let payload: CameraListResponse | null = null;
      try {
        payload = text ? (JSON.parse(text) as CameraListResponse) : null;
      } catch {
        payload = null;
      }

      if (!response.ok) {
        throw new Error(
          (payload as any)?.message ||
            text ||
            `Unable to load cameras (${response.status}).`,
        );
      }

      setCameras(Array.isArray(payload?.items) ? payload!.items! : []);

      if (resolvedRole === "OWNER" || resolvedRole === "INTEGRATOR") {
        try {
          const userResult = await listUsers();
          setUsers(Array.isArray(userResult.items) ? userResult.items : []);
        } catch (error) {
          setUsers(null);
          setUsersError(
            error instanceof Error
              ? error.message
              : "User summary is temporarily unavailable.",
          );
        }
      } else {
        setUsers(null);
      }

      setLastRefreshed(new Date());
    } catch (error) {
      setDashboardError(
        error instanceof Error ? error.message : "Unable to load the dashboard.",
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void loadDashboard(true);
    const refreshTimer = window.setInterval(() => {
      void loadDashboard(false);
    }, 60000);

    return () => window.clearInterval(refreshTimer);
  }, [loadDashboard]);

  const stats = useMemo(() => {
    const total = cameras.length;
    const online = cameras.filter(
      (camera) => getCameraState(camera) === "online",
    ).length;
    const offline = cameras.filter(
      (camera) => getCameraState(camera) === "offline",
    ).length;
    const disabled = cameras.filter(
      (camera) => getCameraState(camera) === "disabled",
    ).length;
    const unknown = cameras.filter(
      (camera) => getCameraState(camera) === "unknown",
    ).length;
    const enabled = total - disabled;
    const attention = offline + unknown + disabled;
    const recording = cameras.filter(hasRecording).length;
    const motion = cameras.filter(
      (camera) => camera.enabled !== false && isMotionCollectionEnabled(camera),
    ).length;
    const withPlan = cameras.filter(
      (camera) => getPlanName(camera).toLowerCase() !== "no plan",
    ).length;
    const assigned = cameras.filter((camera) => Boolean(camera.assignedTo)).length;

    return {
      total,
      enabled,
      online,
      offline,
      disabled,
      unknown,
      attention,
      recording,
      motion,
      withPlan,
      assigned,
      health: percentage(online, enabled),
    };
  }, [cameras]);

  const recentCameras = useMemo(
    () =>
      [...cameras]
        .sort((a, b) => {
          const aTime = new Date(a.updatedAt || a.createdAt || 0).getTime();
          const bTime = new Date(b.updatedAt || b.createdAt || 0).getTime();
          return bTime - aTime;
        })
        .slice(0, 6),
    [cameras],
  );

  const planDistribution = useMemo(() => {
    const plans = new Map<string, number>();
    cameras.forEach((camera) => {
      const plan = getPlanName(camera);
      plans.set(plan, (plans.get(plan) || 0) + 1);
    });

    return Array.from(plans.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, [cameras]);

  const userStats = useMemo(() => {
    if (!users) return null;
    return {
      total: users.length,
      active: users.filter((item) => item.status === "ACTIVE").length,
      integrators: users.filter((item) => item.role === "INTEGRATOR").length,
      admins: users.filter((item) => item.role === "ADMIN").length,
      viewers: users.filter((item) => item.role === "VIEWER").length,
    };
  }, [users]);

  const fleetHeadline =
    stats.total === 0
      ? "No cameras added yet"
      : stats.attention === 0
        ? "Fleet is healthy"
        : `${stats.attention} camera${stats.attention === 1 ? "" : "s"} need attention`;

  const fleetDescription =
    stats.total === 0
      ? "Add your first camera to start monitoring fleet health and service coverage."
      : stats.attention === 0
        ? "Every enabled camera with a known connection state is currently online."
        : "Review offline, disabled, or unknown cameras from the Cameras page.";

  return (
    <div className="dashboard-page">
      <TopBar title="Qumra" onSignOut={onSignOut} email={email} />

      <main className="dashboard-page__content">
        <section className="dashboard-hero">
          <div>
            <p className="dashboard-eyebrow">Operations overview</p>
            <div className="dashboard-title-row">
              <h1>Dashboard</h1>
              <span className="dashboard-role-badge">{roleLabel(role)}</span>
            </div>
            <p className="dashboard-subtitle">
              A live overview of camera health, recording coverage, billing plans,
              and account activity.
            </p>
          </div>

          <div className="dashboard-refresh-area">
            <button
              type="button"
              className="dashboard-refresh-button"
              disabled={refreshing}
              onClick={() => void loadDashboard(false)}
            >
              <span aria-hidden="true" className={refreshing ? "dashboard-refresh-icon dashboard-refresh-icon--active" : "dashboard-refresh-icon"}>↻</span>
              {refreshing ? "Refreshing" : "Refresh"}
            </button>
            <span className="dashboard-refresh-time">
              {lastRefreshed
                ? `Updated ${lastRefreshed.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
                : "Waiting for live data"}
            </span>
          </div>
        </section>

        {dashboardError && (
          <div className="dashboard-alert dashboard-alert--error" role="alert">
            <div>
              <strong>Dashboard data could not be refreshed.</strong>
              <span>{dashboardError}</span>
            </div>
            <button type="button" onClick={() => void loadDashboard(false)}>
              Try again
            </button>
          </div>
        )}

        {loading ? (
          <section className="dashboard-loading" aria-live="polite">
            <div className="dashboard-loading__spinner" />
            <strong>Loading your Qumra environment</strong>
            <span>Checking cameras, plans, and account access…</span>
          </section>
        ) : (
          <>
            <section className="dashboard-summary-grid" aria-label="Fleet summary">
              <article className="dashboard-summary-card">
                <div className="dashboard-summary-card__icon dashboard-summary-card__icon--neutral" aria-hidden="true">C</div>
                <div>
                  <span className="dashboard-summary-card__label">Total cameras</span>
                  <strong>{stats.total}</strong>
                  <small>{stats.enabled} enabled</small>
                </div>
              </article>

              <article className="dashboard-summary-card">
                <div className="dashboard-summary-card__icon dashboard-summary-card__icon--success" aria-hidden="true">✓</div>
                <div>
                  <span className="dashboard-summary-card__label">Online now</span>
                  <strong>{stats.online}</strong>
                  <small>{stats.health}% of enabled fleet</small>
                </div>
              </article>

              <article className="dashboard-summary-card">
                <div className="dashboard-summary-card__icon dashboard-summary-card__icon--warning" aria-hidden="true">!</div>
                <div>
                  <span className="dashboard-summary-card__label">Needs attention</span>
                  <strong>{stats.attention}</strong>
                  <small>{stats.offline} offline · {stats.unknown} unknown · {stats.disabled} disabled</small>
                </div>
              </article>

              <article className="dashboard-summary-card">
                <div className="dashboard-summary-card__icon dashboard-summary-card__icon--recording" aria-hidden="true">●</div>
                <div>
                  <span className="dashboard-summary-card__label">Recording enabled</span>
                  <strong>{stats.recording}</strong>
                  <small>{percentage(stats.recording, stats.total)}% of all cameras</small>
                </div>
              </article>
            </section>

            <section className="dashboard-primary-grid">
              <article className="dashboard-panel dashboard-health-panel">
                <div className="dashboard-panel__header">
                  <div>
                    <p className="dashboard-panel__eyebrow">Fleet health</p>
                    <h2>{fleetHeadline}</h2>
                    <p>{fleetDescription}</p>
                  </div>
                  <Link className="dashboard-text-link" to="/cameras">
                    View cameras <span aria-hidden="true">→</span>
                  </Link>
                </div>

                <div className="dashboard-health-score-row">
                  <div
                    className="dashboard-health-score"
                    style={{ "--dashboard-health": `${stats.health}%` } as React.CSSProperties}
                    aria-label={`${stats.health}% of enabled cameras online`}
                  >
                    <div>
                      <strong>{stats.health}%</strong>
                      <span>online</span>
                    </div>
                  </div>

                  <div className="dashboard-health-breakdown">
                    <div>
                      <span><i className="dashboard-dot dashboard-dot--online" />Online</span>
                      <strong>{stats.online}</strong>
                    </div>
                    <div>
                      <span><i className="dashboard-dot dashboard-dot--offline" />Offline</span>
                      <strong>{stats.offline}</strong>
                    </div>
                    <div>
                      <span><i className="dashboard-dot dashboard-dot--unknown" />Unknown</span>
                      <strong>{stats.unknown}</strong>
                    </div>
                    <div>
                      <span><i className="dashboard-dot dashboard-dot--disabled" />Disabled</span>
                      <strong>{stats.disabled}</strong>
                    </div>
                  </div>
                </div>

                <div className="dashboard-fleet-bar" aria-hidden="true">
                  {stats.total > 0 && (
                    <>
                      <span className="dashboard-fleet-bar__online" style={{ width: `${percentage(stats.online, stats.total)}%` }} />
                      <span className="dashboard-fleet-bar__offline" style={{ width: `${percentage(stats.offline, stats.total)}%` }} />
                      <span className="dashboard-fleet-bar__unknown" style={{ width: `${percentage(stats.unknown, stats.total)}%` }} />
                      <span className="dashboard-fleet-bar__disabled" style={{ width: `${percentage(stats.disabled, stats.total)}%` }} />
                    </>
                  )}
                </div>
              </article>

              <article className="dashboard-panel dashboard-coverage-panel">
                <div className="dashboard-panel__header dashboard-panel__header--compact">
                  <div>
                    <p className="dashboard-panel__eyebrow">Service coverage</p>
                    <h2>Camera capabilities</h2>
                    <p>Current feature coverage across the camera fleet.</p>
                  </div>
                </div>

                <div className="dashboard-coverage-list">
                  <div className="dashboard-coverage-item">
                    <div className="dashboard-coverage-item__top">
                      <span>Cloud recording</span>
                      <strong>{stats.recording}/{stats.total}</strong>
                    </div>
                    <div className="dashboard-progress"><span style={{ width: `${percentage(stats.recording, stats.total)}%` }} /></div>
                  </div>
                  <div className="dashboard-coverage-item">
                    <div className="dashboard-coverage-item__top">
                      <span>Motion collection</span>
                      <strong>{stats.motion}/{stats.total}</strong>
                    </div>
                    <div className="dashboard-progress"><span style={{ width: `${percentage(stats.motion, stats.total)}%` }} /></div>
                  </div>
                  <div className="dashboard-coverage-item">
                    <div className="dashboard-coverage-item__top">
                      <span>Billing plan assigned</span>
                      <strong>{stats.withPlan}/{stats.total}</strong>
                    </div>
                    <div className="dashboard-progress"><span style={{ width: `${percentage(stats.withPlan, stats.total)}%` }} /></div>
                  </div>
                  <div className="dashboard-coverage-item">
                    <div className="dashboard-coverage-item__top">
                      <span>User assignment</span>
                      <strong>{stats.assigned}/{stats.total}</strong>
                    </div>
                    <div className="dashboard-progress"><span style={{ width: `${percentage(stats.assigned, stats.total)}%` }} /></div>
                  </div>
                </div>
              </article>
            </section>

            <section className="dashboard-secondary-grid">
              <article className="dashboard-panel dashboard-activity-panel">
                <div className="dashboard-panel__header">
                  <div>
                    <p className="dashboard-panel__eyebrow">Recent activity</p>
                    <h2>Latest camera updates</h2>
                    <p>The cameras most recently updated in Qumra.</p>
                  </div>
                  <Link className="dashboard-text-link" to="/cameras">Manage fleet <span aria-hidden="true">→</span></Link>
                </div>

                {recentCameras.length === 0 ? (
                  <div className="dashboard-empty-state">
                    <strong>No camera activity yet</strong>
                    <span>Camera updates will appear here after cameras are added.</span>
                    <Link to="/cameras">Go to Cameras</Link>
                  </div>
                ) : (
                  <div className="dashboard-camera-list">
                    {recentCameras.map((camera) => {
                      const state = getCameraState(camera);
                      const cameraId = getCameraId(camera);
                      return (
                        <div className="dashboard-camera-row" key={cameraId || `${getCameraName(camera)}-${camera.updatedAt || camera.createdAt || "camera"}`}>
                          <div className="dashboard-camera-row__identity">
                            <div className="dashboard-camera-glyph" aria-hidden="true">◉</div>
                            <div>
                              <strong>{getCameraName(camera)}</strong>
                              <span>{cameraId ? `ID ${cameraId}` : "Camera"}</span>
                            </div>
                          </div>
                          <span className={`dashboard-status dashboard-status--${state}`}>
                            <i />{stateLabel(state)}
                          </span>
                          <span className="dashboard-camera-plan">{getPlanName(camera)}</span>
                          <span className="dashboard-camera-time">{formatRelativeTime(camera.updatedAt || camera.createdAt)}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </article>

              <div className="dashboard-side-stack">
                <article className="dashboard-panel dashboard-plans-panel">
                  <div className="dashboard-panel__header dashboard-panel__header--compact">
                    <div>
                      <p className="dashboard-panel__eyebrow">Commercial coverage</p>
                      <h2>Plan distribution</h2>
                      <p>Billing plans currently attached to cameras.</p>
                    </div>
                  </div>

                  {planDistribution.length === 0 ? (
                    <div className="dashboard-mini-empty">No cameras to summarize.</div>
                  ) : (
                    <div className="dashboard-plan-list">
                      {planDistribution.slice(0, 5).map((plan) => (
                        <div className="dashboard-plan-row" key={plan.name}>
                          <div>
                            <span>{plan.name}</span>
                            <div className="dashboard-plan-bar"><span style={{ width: `${percentage(plan.count, stats.total)}%` }} /></div>
                          </div>
                          <strong>{plan.count}</strong>
                        </div>
                      ))}
                    </div>
                  )}
                </article>

                <article className="dashboard-panel dashboard-users-panel">
                  <div className="dashboard-panel__header dashboard-panel__header--compact">
                    <div>
                      <p className="dashboard-panel__eyebrow">Account access</p>
                      <h2>User access</h2>
                      <p>{userStats ? "Users provisioned for this tenant." : "Access summary for your portal role."}</p>
                    </div>
                    {(role === "OWNER" || role === "INTEGRATOR") && (
                      <Link className="dashboard-text-link" to="/users">Manage <span aria-hidden="true">→</span></Link>
                    )}
                  </div>

                  {userStats ? (
                    <div className="dashboard-user-summary">
                      <div><strong>{userStats.total}</strong><span>Total</span></div>
                      <div><strong>{userStats.active}</strong><span>Active</span></div>
                      <div><strong>{userStats.integrators}</strong><span>Integrators</span></div>
                      <div><strong>{userStats.admins}</strong><span>Admins</span></div>
                      <div><strong>{userStats.viewers}</strong><span>Viewers</span></div>
                    </div>
                  ) : usersError ? (
                    <div className="dashboard-mini-empty dashboard-mini-empty--warning">User summary unavailable: {usersError}</div>
                  ) : (
                    <div className="dashboard-role-card">
                      <span>Signed in as</span>
                      <strong>{roleLabel(role)}</strong>
                      <p>User management metrics are shown to Owners and Integrators.</p>
                    </div>
                  )}
                </article>
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
